import { destr } from 'destr'
import { events } from './events'
import { useEvent } from './framework'
import { fetchPaginatedCSV } from './fetch-paginated-csv'

const HANDLE = {
  UPHOLSTERY: 'upholstery',
  NO_UPHOLSTERY: 'no-upholstery',
  PLASTIC: 'polypropylene',
}

function getUrlParams() {
  const url = new URL(window.location)
  const data = [...url.searchParams.entries()]
  return data.map(entry => ({ type: entry[0], handle: entry[1] }))
}

let allColorsCache = null
let allProductOptions = null

window.addEventListener(events.window.navigation, () => {
  allColorsCache = null
  allProductOptions = null
})

/**
 * fetch all upholstery
 */
const upholsteryCache = await fetchPaginatedCSV(
  '/?section_id=configurator-all-upholstery-csv'
)
  .then(upholsteryRaw => {
    let upholstery = []

    upholsteryRaw.forEach(item => {
      if (!upholstery.find(family => family.handle === item.parent_handle)) {
        upholstery.push({
          handle: item.parent_handle,
          name: item.parent_name,
          material: item.parent_material,
          colors: [],
        })
      }

      upholstery
        .find(family => family.handle === item.parent_handle)
        .colors.push({ handle: item.handle, name: item.name })
    })

    return upholstery
  })
  .catch(error => {
    console.log(error)
    return []
  })

async function getUpholstery() {
  return upholsteryCache
}

export const getConfiguratorOptions = async () => {
  try {
    const scriptTag = document.querySelector('#configurator-all-options')

    if (!scriptTag) return null

    const json = destr(scriptTag.innerHTML)
    const upholstery = await getUpholstery()

    const jsonMerged = { ...json, upholstery }

    return jsonMerged
  } catch (error) {
    console.warn(error)
    return {}
  }
}

async function fetchProductOptions() {
  if (allProductOptions) {
    return allProductOptions
  }

  try {
    const url = new URL(window.location.pathname, window.location.origin)

    url.searchParams.set('section_id', 'configurator-product-options')

    const fetchedProductOptionsResponse = await fetch(url.toString())

    const productOptions = new DOMParser()
      .parseFromString(await fetchedProductOptionsResponse.text(), 'text/html')
      .querySelector('#configurator-product-options').innerHTML

    allProductOptions = destr(productOptions)

    return allProductOptions
  } catch (error) {
    console.log(error)

    return {}
  }
}

export const getProductConfiguratorData = async () => {
  const productOptions = await fetchProductOptions()
  const colors = await fetchAllColors()

  const productOptionsWithColors = {
    ...productOptions,
    product_options: [
      ...(productOptions.product_options?.map(item => {
        if (item.type !== 'upholstery') return item
        return { ...item, options: colors }
      }) || []),
    ],
  }

  // rm empty/unused options
  const parsed = {
    ...productOptionsWithColors,
    product_options: productOptionsWithColors.product_options.filter(option => {
      return option.options.length > 0
    }),
    variants: productOptionsWithColors.variants?.map(variant => {
      return {
        ...variant,
        options: variant.options.filter(option => {
          return option.handle
        }),
      }
    }),
  }

  return parsed
}

export const getCurrentOptions = async ({ asObject = false } = {}) => {
  const allData = await getConfiguratorOptions()
  const productData = await getProductConfiguratorData()
  const urlData = getUrlParams()

  if (!productData) return null

  let defaultOptions = productData.product_options.map(option => {
    const optionInParam = urlData.find(param => param.type === option.type)

    const { handle, name } = optionInParam
      ? option.options.find(
          option => option.handle === optionInParam?.handle
        ) || {}
      : option.options[0]

    const isUpholstery = option.type === HANDLE.UPHOLSTERY
    const family = {}

    // cumbersome way of saving the name of the currently chosen color name
    if (isUpholstery) {
      family.handle = allData?.upholstery?.find(type => {
        const color = type.colors?.find(color => color.handle === handle)

        if (color) {
          family.name = color.name
          family.material = type.material
        }

        return color
      })?.handle
    }

    return {
      type: option.type,
      handle,
      family: family.handle,
      name: family.name || name,
      material: family.material,
    }
  })

  /**
   * make plastic option work
   */
  const defaultShellOption = defaultOptions.find(
    option => option.type === 'shell'
  )
  const defaultUpholsteryOption = defaultOptions.find(
    option => option.type === 'upholstery'
  )
  const isPlastic = defaultShellOption?.handle === HANDLE.PLASTIC

  if (isPlastic && defaultUpholsteryOption.family !== HANDLE.PLASTIC) {
    defaultOptions = defaultOptions.map(option => {
      if (option.type !== HANDLE.UPHOLSTERY) return option

      const plastic = allData.upholstery?.find(
        option => option.handle === HANDLE.PLASTIC
      )

      if (plastic) {
        return {
          type: HANDLE.UPHOLSTERY,
          ...plastic.colors[0],
        }
      }

      return option
    })
  }

  return asObject
    ? defaultOptions.reduce((acc, cur) => {
        return { ...acc, [cur.type]: cur }
      }, {})
    : defaultOptions
}

export const setOption = (type, handle) => {
  const url = new URL(window.location)

  const isNoUpholsteryOption = handle =>
    handle === HANDLE.NO_UPHOLSTERY || handle === HANDLE.PLASTIC

  const currentShellOption = url.searchParams.get('shell')

  if (handle === 'null') {
    url.searchParams.delete(type)
  } else {
    url.searchParams.set(type, handle)

    if (type === 'shell' && isNoUpholsteryOption(handle)) {
      url.searchParams.delete('upholstery')
    }

    // remove any existing "upholstery" option if we go from any upholstery-shell to any no-upholstery-shell,
    // or vice versa
    if (
      type === 'shell' &&
      isNoUpholsteryOption(handle) !== isNoUpholsteryOption(currentShellOption)
    ) {
      url.searchParams.delete('upholstery')
    }
  }

  window.history.replaceState(null, '', url.toString())

  useEvent.dispatch(events.configurator.optionUpdate)

  return true
}

/**
 * returns a variant if all current options matches that variant
 */
export const getChosenVariant = async () => {
  const productData = await getProductConfiguratorData()
  const currentOptions = await getCurrentOptions({ asObject: true })

  // return the variant that matches all current options
  return productData.variants?.find(variant => {
    if (!variant?.options.length) return false

    return variant?.options?.every(option => {
      return currentOptions[option.type]?.handle === option?.handle
    })
  })
}

/**
 * returns weather or not the state is to only show available variants' options
 */
export const shouldShowOnlyAvailableOnlineOptions = () => {
  const url = new URL(window.location)

  if (!checkbox.checked) {
    url.searchParams.delete('available', 'true')
  } else {
    url.searchParams.set('available', 'true')
  }
}

/**
 * change ?variant=12345 to all that variant's options; ?frame=oak&upholstery=dune
 */
export const translateUrlVariantToOptions = async () => {
  const productData = await getProductConfiguratorData()

  const url = new URL(window.location)
  const variantId = url.searchParams.get('variant')

  if (!variantId) return

  const id = parseInt(variantId)
  const variant = productData?.variants?.find(variant => variant.id === id)

  if (!variant) return

  variant.options.forEach(option => {
    setOption(option.type, option.handle)
  })

  const newUrl = new URL(window.location)
  newUrl.searchParams.delete('variant')

  window.history.replaceState(null, '', newUrl.toString())
}

async function fetchAllColors() {
  allColorsCache =
    allColorsCache ||
    fetchPaginatedCSV(
      window.location.pathname +
        '/?section_id=configurator-product-upholstery-csv'
    ).then(productUpholsteryRaw => {
      let productUpholstery = []

      productUpholsteryRaw.forEach(item => {
        productUpholstery.push({
          type: item.type,
          family: item.parent_handle,
          handle: item.handle,
          material: item.parent_material,
          name: item.name,
        })
      })

      return productUpholstery
    })

  return allColorsCache
}
